/**
 * صفحة طلباتي الموحدة — تبويبان: عقود الصيانة | الأراضي المتاحة.
 * Route: /my-requests
 * Query: ?tab=maintenance | ?tab=lands
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs, { type Crumb } from "../../../components/navigation/Breadcrumbs";
import { asErr } from "../../../utils/apiHelpers";
import { isAuthenticated } from "../../../services/authService";
import * as donorService from "../../../services/donorService";
import {
  listSupplyRequestsByDonor,
  type SupplyRequestListItem,
} from "../../../services/mosqueDonationService";
import {
  listHygieneSupplyRequestsByDonor,
  type HygieneSupplyRequestListItem,
} from "../../../services/hygieneDonationService";
import { listDonationRequests, type DonationRequestItem } from "../../../services/donationRequestService";
import type { DonorRow } from "../../../types/donor";
import { badgeClassForLandRequestStage, labelForLandRequestStage } from "../../../utils/donationRequestUiStages";

type TabId = "maintenance" | "hygiene" | "lands";

function landDonationStageBadge(stage?: string, state?: string) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClassForLandRequestStage(stage, state)}`}
    >
      {labelForLandRequestStage(stage, state)}
    </span>
  );
}

const numberSar = (v: number | undefined | null) =>
  typeof v === "number" && !isNaN(v) ? v.toLocaleString("en-US") : "-";

export default function UnifiedMyRequestsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as TabId | null;
  const donorParam = searchParams.get("donor") || "";
  const activeTab: TabId =
    tabParam === "lands" ? "lands" : tabParam === "hygiene" ? "hygiene" : "maintenance";
  const setActiveTab = (t: TabId) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("tab", t);
      if (selectedDonorToken) next.set("donor", selectedDonorToken);
      return next;
    });
  };
  const updateUrlWithDonor = (token: string) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      if (token) next.set("donor", token);
      else next.delete("donor");
      return next;
    });
  };

  const loggedIn = isAuthenticated();

  const [donors, setDonors] = useState<DonorRow[]>([]);
  const [selectedDonorToken, setSelectedDonorToken] = useState<string>("");
  const [loadingDonors, setLoadingDonors] = useState(false);

  const [mosqueRequests, setMosqueRequests] = useState<SupplyRequestListItem[]>([]);
  const [mosqueRequestsTotalCount, setMosqueRequestsTotalCount] = useState<number | null>(null);
  const [loadingMosqueReqs, setLoadingMosqueReqs] = useState(false);
  const [mosqueReqsError, setMosqueReqsError] = useState<string | null>(null);

  const [hygieneRequests, setHygieneRequests] = useState<HygieneSupplyRequestListItem[]>([]);
  const [hygieneRequestsTotalCount, setHygieneRequestsTotalCount] = useState<number | null>(null);
  const [loadingHygieneReqs, setLoadingHygieneReqs] = useState(false);
  const [hygieneReqsError, setHygieneReqsError] = useState<string | null>(null);

  const [landRequests, setLandRequests] = useState<DonationRequestItem[]>([]);
  const [loadingLandReqs, setLoadingLandReqs] = useState(false);
  const [landReqsError, setLandReqsError] = useState<string | null>(null);
  const [landSearchQ, setLandSearchQ] = useState("");

  const crumbs: Crumb[] = [
    { label: "الرئيسية", to: "/" },
    { label: "لوحة المؤشرات", to: "/my-dashboard" },
    { label: "طلباتي", to: null },
  ];

  useEffect(() => {
    if (!loggedIn) {
      navigate(`/benefaction/login?returnUrl=${encodeURIComponent("/my-requests")}`, { replace: true });
      return;
    }
    let mounted = true;
    (async () => {
      try {
        setLoadingDonors(true);
        const allDonors = (await donorService.listMine()) as DonorRow[];
        const list =
          activeTab === "lands"
            ? allDonors
            : donorService.scopeDonorsToCurrentUser(allDonors);
        if (!mounted) return;
        setDonors(list);
        const fromUrl = donorParam && list.some((d) => (d.donorIdToken ?? (d as any).donorIdToken) === donorParam)
          ? donorParam
          : "";
        const ownerToken =
          donorService.pickDonorForCurrentUser(list)?.donorIdToken ??
          list[0]?.donorIdToken ??
          (list[0] as any)?.donorIdToken ??
          "";
        const initial = fromUrl || ownerToken;
        setSelectedDonorToken(initial);
        if (initial !== donorParam) {
          setSearchParams((p) => {
            const next = new URLSearchParams(p);
            if (initial) next.set("donor", initial);
            else next.delete("donor");
            return next;
          });
        }
      } catch {
        if (mounted) setDonors([]);
      } finally {
        if (mounted) setLoadingDonors(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [loggedIn, navigate, donorParam, activeTab, setSearchParams]);

  useEffect(() => {
    if (!loggedIn || !selectedDonorToken || activeTab !== "maintenance") {
      setMosqueRequests([]);
      setMosqueRequestsTotalCount(null);
      setMosqueReqsError(null);
      return;
    }
    let closed = false;
    (async () => {
      try {
        setLoadingMosqueReqs(true);
        setMosqueReqsError(null);
        const result = await listSupplyRequestsByDonor({
          DonorIdToken: selectedDonorToken,
          PageNo: 1,
          PageSize: 500,
        });
        if (!closed) {
          setMosqueRequests(result.data ?? []);
          setMosqueRequestsTotalCount(
            typeof result.count === "number" && result.count >= 0
              ? result.count
              : (result.data ?? []).length
          );
        }
      } catch (e) {
        if (!closed) {
          setMosqueReqsError(asErr(e, "تعذر تحميل طلبات المساجد").message);
          setMosqueRequests([]);
          setMosqueRequestsTotalCount(null);
        }
      } finally {
        if (!closed) setLoadingMosqueReqs(false);
      }
    })();
    return () => {
      closed = true;
    };
  }, [loggedIn, selectedDonorToken, activeTab]);

  useEffect(() => {
    if (!loggedIn || !selectedDonorToken || activeTab !== "hygiene") {
      setHygieneRequests([]);
      setHygieneRequestsTotalCount(null);
      setHygieneReqsError(null);
      return;
    }
    let closed = false;
    (async () => {
      try {
        setLoadingHygieneReqs(true);
        setHygieneReqsError(null);
        const result = await listHygieneSupplyRequestsByDonor({
          DonorIdToken: selectedDonorToken,
          PageNo: 1,
          PageSize: 500,
        });
        if (!closed) {
          setHygieneRequests(result.data ?? []);
          setHygieneRequestsTotalCount(
            typeof result.count === "number" && result.count >= 0
              ? result.count
              : (result.data ?? []).length
          );
        }
      } catch (e) {
        if (!closed) {
          setHygieneReqsError(asErr(e, "تعذر تحميل طلبات النظافة والتعقيم").message);
          setHygieneRequests([]);
          setHygieneRequestsTotalCount(null);
        }
      } finally {
        if (!closed) setLoadingHygieneReqs(false);
      }
    })();
    return () => {
      closed = true;
    };
  }, [loggedIn, selectedDonorToken, activeTab]);

  useEffect(() => {
    if (!loggedIn || !selectedDonorToken || activeTab !== "lands") {
      setLandRequests([]);
      setLandReqsError(null);
      return;
    }
    let closed = false;
    (async () => {
      try {
        setLoadingLandReqs(true);
        setLandReqsError(null);
        const rows = await listDonationRequests(selectedDonorToken, undefined);
        if (!closed) {
          const sorted = [...rows].sort((a, b) => {
            const na = parseInt(String(a.code || "").replace(/\D+/g, "") || "0", 10);
            const nb = parseInt(String(b.code || "").replace(/\D+/g, "") || "0", 10);
            return nb - na;
          });
          setLandRequests(sorted);
        }
      } catch (e) {
        if (!closed) {
          setLandReqsError(asErr(e, "تعذر تحميل طلبات الأراضي").message);
          setLandRequests([]);
        }
      } finally {
        if (!closed) setLoadingLandReqs(false);
      }
    })();
    return () => {
      closed = true;
    };
  }, [loggedIn, selectedDonorToken, activeTab]);

  const filteredLandRequests = useMemo(() => {
    if (!landSearchQ.trim()) return landRequests;
    const needle = landSearchQ.trim().toLowerCase();
    return landRequests.filter(
      (r) =>
        (r.code || "").toLowerCase().includes(needle) ||
        (r.landCode || "").toLowerCase().includes(needle) ||
        (r.stageName || r.state || "").toLowerCase().includes(needle)
    );
  }, [landRequests, landSearchQ]);

  if (!loggedIn) return null;

  const TABS: { id: TabId; label: string }[] = [
    { id: "maintenance", label: "طلبات عقود الصيانة" },
    { id: "hygiene", label: "طلبات أعمال النظافة والتعقيم" },
    { id: "lands", label: "طلبات الأراضي المتاحة" },
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <Breadcrumbs items={crumbs} />

        <div className="rounded-3xl border border-gray-200 bg-white/90 shadow-xl overflow-hidden mt-6">
          <div className="px-4 md:px-6 py-6 border-b border-gray-100 bg-white/75">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h1 className="text-2xl font-semibold text-neutral-900">طلباتي</h1>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center min-h-[44px] rounded-xl bg-sa-600 px-4 py-2 text-sm font-medium text-white hover:bg-sa-700 hover:shadow-md active:scale-[0.98] transition-all duration-200"
                  onClick={() => navigate("/my-dashboard")}
                >
                  لوحة المؤشرات
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center min-h-[44px] rounded-xl border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 hover:border-neutral-400 active:scale-[0.98] transition-all duration-200"
                  onClick={() => {
                    if (activeTab === "maintenance") navigate("/service/mosque-contribution");
                    else if (activeTab === "hygiene") navigate("/mosque/donation-opportunities?catAId=900001");
                    else navigate("/benefaction/lands");
                  }}
                >
                  تقديم طلب جديد
                </button>
              </div>
            </div>
          </div>

          <nav
            className="px-4 md:px-6 py-3 border-b border-gray-100 bg-white/80 flex flex-wrap items-center justify-center gap-2"
            aria-label="نوع الطلبات"
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`min-h-[44px] rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab.id
                    ? "bg-sa-100 text-sa-700 shadow-inner border border-sa-200"
                    : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 border border-transparent active:scale-[0.98]"
                }`}
                onClick={() => setActiveTab(tab.id)}
                aria-pressed={activeTab === tab.id}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="p-4 md:p-6 min-w-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-end mb-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">اسم المستفيد</label>
                <select
                  className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sa-300"
                  dir="rtl"
                  value={selectedDonorToken}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedDonorToken(v);
                    updateUrlWithDonor(v);
                  }}
                  disabled={loadingDonors}
                >
                  {donors.map((d) => (
                    <option key={d.donorIdToken} value={d.donorIdToken}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {donors.length === 0 && !loadingDonors && (
                  <div className="mt-1 text-sm text-red-600">لا يوجد مستفيدون</div>
                )}
              </div>
              <div className="rounded-2xl border border-gray-200 bg-sa-50 p-3 text-sm text-neutral-700">
                عدد الطلبات:{" "}
                <span className="font-semibold text-neutral-900">
                  {activeTab === "maintenance"
                    ? loadingMosqueReqs
                      ? "…"
                      : mosqueRequestsTotalCount ?? mosqueRequests.length
                    : activeTab === "hygiene"
                      ? loadingHygieneReqs
                        ? "…"
                        : hygieneRequestsTotalCount ?? hygieneRequests.length
                      : loadingLandReqs
                        ? "…"
                        : filteredLandRequests.length}
                </span>
              </div>
            </div>

            {activeTab === "maintenance" && (
              <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-neutral-200 text-center">
                  <div className="text-base font-semibold text-neutral-800">طلبات عقود الصيانة القائمة</div>
                </div>
                <div className="p-5">
                  {mosqueReqsError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <span>{mosqueReqsError}</span>
                      <button
                        type="button"
                        className="shrink-0 rounded-xl border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-200"
                        onClick={() => {
                          setMosqueReqsError(null);
                          setLoadingMosqueReqs(true);
                          listSupplyRequestsByDonor({
                            DonorIdToken: selectedDonorToken,
                            PageNo: 1,
                            PageSize: 500,
                          })
                            .then((result) => {
                              setMosqueRequests(result.data ?? []);
                              setMosqueRequestsTotalCount(
                                typeof result.count === "number" && result.count >= 0
                                  ? result.count
                                  : (result.data ?? []).length
                              );
                            })
                            .catch((e) =>
                              setMosqueReqsError(asErr(e, "تعذر تحميل طلبات المساجد").message)
                            )
                            .finally(() => setLoadingMosqueReqs(false));
                        }}
                        disabled={loadingMosqueReqs}
                      >
                        إعادة المحاولة
                      </button>
                    </div>
                  )}
                  {loadingMosqueReqs && <div className="text-neutral-500">جاري التحميل...</div>}
                  {!loadingMosqueReqs && mosqueRequests.length === 0 && !mosqueReqsError && (
                    <div className="text-neutral-500 py-4">
                      لا توجد طلبات عقود صيانة. يمكنك تقديم طلب جديد من فرص عقود الصيانة.
                      <button
                        type="button"
                        className="mr-2 mt-2 rounded-xl bg-sa-600 px-4 py-2 text-sm font-medium text-white hover:bg-sa-700"
                        onClick={() => navigate("/service/mosque-contribution")}
                      >
                        فرص التبرع
                      </button>
                    </div>
                  )}
                  {!loadingMosqueReqs && mosqueRequests.length > 0 && (
                    <div className="overflow-x-auto rounded-xl border border-neutral-200 w-full max-w-full">
                      <table className="w-full min-w-[800px] text-sm">
                        <thead>
                          <tr className="text-center bg-neutral-50 text-neutral-600">
                            <th className="py-2.5 px-3 font-medium">رقم الطلب</th>
                            <th className="py-2.5 px-3 font-medium">رقم المسجد</th>
                            <th className="py-2.5 px-3 font-medium">اسم المسجد</th>
                            <th className="py-2.5 px-3 font-medium">التاريخ</th>
                            <th className="py-2.5 px-3 font-medium">حالة الطلب</th>
                            <th className="py-2.5 px-3 font-medium">اسم مقدم الطلب</th>
                            <th className="py-2.5 px-3 font-medium">عرض</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-200">
                          {mosqueRequests.map((row) => (
                            <tr key={row.supplyId} className="hover:bg-neutral-50 align-top">
                              <td className="py-2.5 px-3 text-center text-neutral-900 whitespace-nowrap">
                                {row.supplyName ?? row.requestName ?? `DSR-${row.supplyId}`}
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-800 whitespace-nowrap">
                                {row.number ?? "—"}
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-800 max-w-[200px]">
                                <span className="block leading-snug truncate" title={row.mosqueName ?? undefined}>{row.mosqueName ?? "—"}</span>
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-700 whitespace-nowrap">
                                {row.supplyDate ?? "—"}
                              </td>
                              <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                {landDonationStageBadge(row.state ?? row.status, undefined)}
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-700 max-w-[220px]">
                                <span className="block leading-snug break-words" title={row.partnerName ?? undefined}>
                                  {row.partnerName ?? "—"}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                {(row.ddrId ?? (row as any).ddrId) ? (
                                  <button
                                    type="button"
                                    className="inline-flex items-center rounded-xl bg-sa-600 px-3 py-1.5 text-white text-xs sm:text-sm font-medium shadow-sm hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-300"
                                    onClick={() =>
                                      navigate(`/mosque/donation-request/${row.ddrId ?? (row as any).ddrId}`, {
                                        state: {
                                          fromMyRequests: true,
                                          donorToken: selectedDonorToken,
                                          backTo: "/my-requests",
                                          contributionSerial: row.supplyName ?? row.requestName ?? null,
                                        },
                                      })
                                    }
                                  >
                                    عرض
                                  </button>
                                ) : (
                                  <span className="text-neutral-400 text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "hygiene" && (
              <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-neutral-200 text-center">
                  <div className="text-base font-semibold text-neutral-800">طلبات أعمال النظافة والتعقيم القائمة</div>
                </div>
                <div className="p-4">
                  {hygieneReqsError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm mb-4">
                      {hygieneReqsError}
                    </div>
                  )}
                  {loadingHygieneReqs && <div className="text-neutral-500">جاري التحميل...</div>}
                  {!loadingHygieneReqs && hygieneRequests.length === 0 && !hygieneReqsError && (
                    <div className="text-neutral-500 py-4">
                      لا توجد طلبات أعمال النظافة والتعقيم. يمكنك تقديم طلب جديد من فرص النظافة و التعقيم.
                      <button
                        type="button"
                        className="mr-2 mt-2 rounded-xl bg-sa-600 px-4 py-2 text-sm font-medium text-white hover:bg-sa-700"
                        onClick={() => navigate("/mosque/donation-opportunities?catAId=900001")}
                      >
                        فرص النظافة والتعقيم
                      </button>
                    </div>
                  )}
                  {!loadingHygieneReqs && hygieneRequests.length > 0 && (
                    <div className="overflow-x-auto rounded-xl border border-neutral-200 w-full max-w-full">
                      <table className="w-full min-w-[800px] text-sm">
                        <thead>
                          <tr className="text-center bg-neutral-50 text-neutral-600">
                            <th className="py-2.5 px-3 font-medium">رقم الطلب</th>
                            <th className="py-2.5 px-3 font-medium">رقم المسجد</th>
                            <th className="py-2.5 px-3 font-medium">اسم المسجد</th>
                            <th className="py-2.5 px-3 font-medium">التاريخ</th>
                            <th className="py-2.5 px-3 font-medium">حالة الطلب</th>
                            <th className="py-2.5 px-3 font-medium">اسم مقدم الطلب</th>
                            <th className="py-2.5 px-3 font-medium">عرض</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-200">
                          {hygieneRequests.map((row) => (
                            <tr key={row.supplyId} className="hover:bg-neutral-50 align-top">
                              <td className="py-2.5 px-3 text-center text-neutral-900 whitespace-nowrap">
                                {row.supplyName ?? row.requestName ?? `DSR-${row.supplyId}`}
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-800 whitespace-nowrap">
                                {row.number ?? "—"}
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-800 max-w-[200px]">
                                <span className="block leading-snug break-words" title={row.mosqueName ?? undefined}>{row.mosqueName ?? "—"}</span>
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-700 whitespace-nowrap">
                                {row.supplyDate ?? "—"}
                              </td>
                              <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                {landDonationStageBadge(row.state ?? row.status, undefined)}
                              </td>
                              <td className="py-2.5 px-3 text-center text-neutral-700 max-w-[220px]">
                                <span className="block leading-snug break-words" title={row.partnerName ?? undefined}>
                                  {row.partnerName ?? "—"}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                {(row.ddrId ?? (row as any).ddrId) ? (
                                  <button
                                    type="button"
                                    className="inline-flex items-center rounded-xl bg-sa-600 px-3 py-1.5 text-white text-xs sm:text-sm font-medium shadow-sm hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-300"
                                    onClick={() =>
                                      navigate(`/mosque/donation-request/${row.ddrId ?? (row as any).ddrId}`, {
                                        state: {
                                          fromMyRequests: true,
                                          donorToken: selectedDonorToken,
                                          backTo: "/my-requests",
                                          source: "hygiene" as const,
                                        },
                                      })
                                    }
                                  >
                                    عرض
                                  </button>
                                ) : (
                                  <span className="text-neutral-400 text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "lands" && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">بحث</label>
                  <input
                    className="block w-full max-w-md rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sa-300"
                    dir="rtl"
                    value={landSearchQ}
                    onChange={(e) => setLandSearchQ(e.target.value)}
                    placeholder="ابحث برقم الطلب أو الأرض"
                  />
                </div>
                <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 sm:px-6 py-3 border-b border-neutral-200">
                    <div className="text-sm font-semibold text-neutral-800">طلبات الأراضي المتاحة</div>
                  </div>
                  <div className="p-4 sm:p-6">
                    {landReqsError && (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm mb-4">
                        {landReqsError}
                      </div>
                    )}
                    {loadingLandReqs && <div className="text-neutral-500">جاري التحميل...</div>}
                    {!loadingLandReqs && filteredLandRequests.length === 0 && !landReqsError && (
                      <div className="text-neutral-500 py-4">
                        لا توجد طلبات أراضي. يمكنك تقديم طلب جديد من الأراضي المتاحة.
                        <button
                          type="button"
                          className="mr-2 mt-2 rounded-xl bg-sa-600 px-4 py-2 text-sm font-medium text-white hover:bg-sa-700"
                          onClick={() => navigate("/benefaction/lands")}
                        >
                          الأراضي المتاحة
                        </button>
                      </div>
                    )}
                    {!loadingLandReqs && filteredLandRequests.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-start bg-neutral-50 text-neutral-600">
                              <th className="py-2 px-3 font-medium">رقم الطلب</th>
                              <th className="py-2 px-3 font-medium">حالة الطلب</th>
                              <th className="py-2 px-3 font-medium">الأرض</th>
                              <th className="py-2 px-3 font-medium text-end">المبلغ المطلوب</th>
                              <th className="py-2 px-3 font-medium text-end">المبلغ النهائي</th>
                              <th className="py-2 px-3 font-medium text-end">عرض</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-neutral-200">
                            {filteredLandRequests.map((r) => (
                              <tr key={r.tokenReqId} className="hover:bg-neutral-50">
                                <td className="py-2.5 px-3 text-neutral-900">{r.code}</td>
                                <td className="py-2.5 px-3">{landDonationStageBadge(r.stageName, r.state)}</td>
                                <td className="py-2.5 px-3 text-neutral-800">
                                  {r.landCode || (r.landId ? `#${r.landId}` : "-")}
                                </td>
                                <td className="py-2.5 px-3 text-end tabular-nums">
                                  <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                    {numberSar(r.requestedAmount)}
                                    {/* Use the same riyal icon as the rest of the app. */}
                                    <img
                                      src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                                      alt="ريال سعودي"
                                      className="riyal-icon h-4 w-4"
                                    />
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-end tabular-nums">
                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                  {numberSar(r.finalAmount)}
                                  {/* Use the same riyal icon as the rest of the app. */}
                                  <img
                                    src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                                    alt="ريال سعودي"
                                    className="riyal-icon h-4 w-4"
                                  />
                                </span>
                              </td>
                                <td className="py-2.5 px-3 text-end">
                                  <button
                                    type="button"
                                    className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-xl bg-sa-600 px-3 py-1.5 text-white text-xs sm:text-sm font-medium shadow-sm hover:bg-sa-700 hover:shadow-md active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sa-300"
                                    onClick={() =>
                                      navigate(
                                        `/benefaction/request/${r.landId}?req=${encodeURIComponent(String(r.tokenReqId || ""))}&donor=${encodeURIComponent(String(selectedDonorToken || ""))}`
                                      )
                                    }
                                  >
                                    عرض
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
